import React, { useCallback, useMemo, useState } from "react";
import { ExerciseTypeContainer } from "./ExerciseTypeContainer";
import AudioRecorder from "@/components/audio-recorder";
import {
  ExerciseItem,
  EvaluateExerciseResponse,
  getExerciseContent,
} from "@/services/student/exercise.interfaces";
import { useExercise } from "../adapter/StudentExerciseContext";
import {
  apiPollSpeechEvaluation,
  SpeechEvaluationStatus,
} from "@/services/student/speech-evaluation.api";
import { Button } from "@/components/ui/button";

type SpeakTheSentenceProps = {
  item: ExerciseItem;
  moduleId?: string;
};

type SpeakUiState =
  | { kind: "idle" }
  | { kind: "evaluating"; answerId: string }
  | {
      kind: "completed";
      contentScore: number;
      pronunciationScore: number;
      summary: string;
    }
  | { kind: "failed"; message: string };

const blobToBase64 = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result;
      if (typeof result !== "string") {
        reject(new Error("Failed to read audio"));
        return;
      }
      const comma = result.indexOf(",");
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(reader.error ?? new Error("read failed"));
    reader.readAsDataURL(blob);
  });

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

async function pollUntilTerminal(
  answerId: string,
  maxAttempts = 40,
  intervalMs = 1500
): Promise<SpeechEvaluationStatus> {
  let last: SpeechEvaluationStatus | null = null;
  for (let i = 0; i < maxAttempts; i++) {
    last = await apiPollSpeechEvaluation(answerId);
    if (last.status === "Completed" || last.status === "Failed") {
      return last;
    }
    await sleep(intervalMs);
  }
  return (
    last ?? {
      answerId,
      status: "Failed",
      error: "Tempo esgotado ao avaliar. Tente novamente.",
      isCorrect: false,
    }
  );
}

export const SpeakTheSentence: React.FC<SpeakTheSentenceProps> = ({
  item,
  moduleId: moduleIdProp,
}) => {
  const moduleId = moduleIdProp ?? item.moduleId ?? "";
  const content = useMemo(
    () =>
      getExerciseContent<{
        sentence?: string;
        question?: string;
        text?: string;
        instructions?: string;
      }>(item),
    [item]
  );

  const targetSentence =
    content.sentence ||
    content.question ||
    content.text ||
    item.question ||
    item.title ||
    "—";

  return (
    <ExerciseTypeContainer
      item={{
        id: item.id,
        type: "speak_the_sentence",
        moduleId,
      }}
      moduleId={moduleId}
      title1="Fale a frase:"
      title2={targetSentence}
      exerciseNumber={item.name || item.title || item.id}
      hideVerifyButton
    >
      <SpeakTheSentenceBody item={item} moduleId={moduleId} />
    </ExerciseTypeContainer>
  );
};

const SpeakTheSentenceBody: React.FC<SpeakTheSentenceProps> = ({
  item,
  moduleId: moduleIdProp,
}) => {
  const { verify, moduleId: sessionModuleId } = useExercise();
  const moduleId = moduleIdProp ?? sessionModuleId ?? item.moduleId ?? "";

  const [ui, setUi] = useState<SpeakUiState>({ kind: "idle" });

  const handleSubmit = useCallback(
    async (blob: Blob) => {
      if (!moduleId) {
        setUi({ kind: "failed", message: "Módulo não encontrado." });
        return;
      }

      setUi({ kind: "evaluating", answerId: "" });

      try {
        const audioBase64 = await blobToBase64(blob);
        const evaluation = (await verify({
          audioBase64,
          audioFormat: "webm",
        })) as EvaluateExerciseResponse & {
          speechEvaluationStatus?: string;
          answerId?: string;
          contentScore?: number;
          pronunciationScore?: number;
          summary?: string;
        };

        const answerId = evaluation.answerId;
        if (!answerId) {
          setUi({
            kind: "failed",
            message: "Não foi possível iniciar a avaliação.",
          });
          return;
        }

        if (evaluation.speechEvaluationStatus === "Completed") {
          setUi({
            kind: "completed",
            contentScore: evaluation.contentScore ?? 0,
            pronunciationScore: evaluation.pronunciationScore ?? 0,
            summary: evaluation.summary ?? evaluation.feedback ?? "",
          });
          return;
        }

        setUi({ kind: "evaluating", answerId });
        const terminal = await pollUntilTerminal(answerId);

        if (terminal.status === "Completed") {
          setUi({
            kind: "completed",
            contentScore: terminal.contentScore ?? 0,
            pronunciationScore: terminal.pronunciationScore ?? 0,
            summary: terminal.summary ?? terminal.feedback ?? "",
          });
          return;
        }

        setUi({
          kind: "failed",
          message:
            terminal.error ||
            terminal.feedback ||
            "Falha na avaliação. Tente novamente.",
        });
      } catch (err) {
        setUi({
          kind: "failed",
          message:
            err instanceof Error
              ? err.message
              : "Falha na avaliação. Tente novamente.",
        });
      }
    },
    [verify, moduleId]
  );

  return (
    <>
      {ui.kind === "evaluating" && (
        <p className="mb-4 text-center text-sm text-muted-foreground">
          Avaliando…
        </p>
      )}

      {ui.kind === "completed" && (
        <div className="mb-4 space-y-2 rounded-lg border p-4 text-sm">
          <p>
            <span className="font-medium">Conteúdo:</span> {ui.contentScore}/100
          </p>
          <p>
            <span className="font-medium">Pronúncia:</span>{" "}
            {ui.pronunciationScore}/100
          </p>
          <p className="text-muted-foreground">{ui.summary}</p>
        </div>
      )}

      {ui.kind === "failed" && (
        <div className="mb-4 space-y-2 rounded-lg border border-destructive/40 p-4 text-sm">
          <p>{ui.message}</p>
          <Button type="button" variant="outline" onClick={() => setUi({ kind: "idle" })}>
            Tentar de novo
          </Button>
        </div>
      )}

      {(ui.kind === "idle" || ui.kind === "failed") && (
        <AudioRecorder onSubmit={handleSubmit} />
      )}

      {ui.kind === "evaluating" && (
        <AudioRecorder onSubmit={undefined} disabled />
      )}
    </>
  );
};
